import { css } from "@emotion/react";
import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, DesignToken, Text } from "@livechat/design-system-react-components";

import { config } from "config/config";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { prepareMailboxAddress } from "helpers/mailboxes";
import ProviderAccordions, {
  Location,
} from "routes/settings/inboxes/forwarding-addresses/instructions-modal/ProviderAccordions";
import { SettingsHeader, SettingsParagraph } from "routes/settings/styled";
import { selectDefaultMailboxUsed, selectLicenseId } from "store/entities/license";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { SettingsViewActions } from "store/views/settings";
import { withVar } from "themes/helpers";

import { SetupBanner } from "./SetupBanner";
import { SubscriptionBanner } from "./SubscriptionBanner";

const activeContent = css`
  padding-top: 40px;
`;

const sectionDescription = css`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  font-size: 15px;
  line-height: 22px;
  padding-bottom: 10px;
`;

const header = css`
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicTertiary)};
`;

export const LiveChatForwardingInstructions: FC = () => {
  const inboxAddress = prepareMailboxAddress(useSelector(selectLicenseId));
  const subscriptionState = useSelector(selectCurrentSubscription).status;
  const defaultInboxUsed = useSelector(selectDefaultMailboxUsed);
  const dispatch = useDispatch();
  const handleForwardingAddressCopy = (): void => {
    dispatch(SettingsViewActions.forwardingAddressCopied());
  };

  useEffect(() => {
    trackEngagement("LC settings: Email by HelpDesk section opened", EventPlace.LiveChat);
  }, []);

  return (
    <>
      <div>
        <Text>
          HelpDesk is an advanced yet simple ticketing system for managing your asynchronous communications, such as
          emails, and is deeply integrated with LiveChat.
        </Text>
        {!defaultInboxUsed && <SetupBanner />}
        {subscriptionState === "expired" && <SubscriptionBanner />}
        {subscriptionState === "active" && (
          <>
            <div css={activeContent} data-testid="lc-forwarding-instructions">
              <SettingsHeader css={header}>Forward your emails to HelpDesk</SettingsHeader>
              <SettingsParagraph>
                <Text css={sectionDescription}>Choose your email provider</Text>
              </SettingsParagraph>
              <ProviderAccordions
                inboxAddress={inboxAddress}
                onForwardingAddressCopy={handleForwardingAddressCopy}
                location={Location.LiveChat}
              />
            </div>
            <SettingsHeader css={header}>Manage your HelpDesk</SettingsHeader>
            <SettingsParagraph css={sectionDescription}>
              Access advanced HelpDesk settings for your team, tickets, channels, and more.
            </SettingsParagraph>
            <Button
              href={`${config.appUrl}/settings/email-addresses`}
              target="_blank"
              rel="noopener noreferrer"
              kind="secondary"
              size="compact"
            >
              Manage HelpDesk
            </Button>
          </>
        )}
      </div>
    </>
  );
};
